// ─────────────────────────────────────────────────────────────────────────────
// useChangeLocation.js
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth }       from "../contexts/AuthContext";
import { logAuditEvent } from "../utils/logAudit";

import { assetService }    from "../services/assetService";
import { locationService } from "../services/locationService";
import { preferencesService } from "../services/preferencesService";
import { singleValueListService } from "../services/singleValueListService";

import {
  // ── Constants & enums ──
  USER_ROLES,
  CSV_EXPORT_HEADERS,
  PREFERENCE_TYPES,
  ASSET_TYPES,
  AUDIT_ACTION_TYPES,
  AUDIT_ENTITY_TYPES,

  // ── Domain predicates ──
  isAssetDisposed,
  isNonSerializedAsset,
  isPrimaryAsset,
  isConsumableAsset,
  isSparePartAsset,
  isMovableChildAsset,
  isExcludedFromMove,
  isLocationActive,

   // ── Formatters & factories ──
  createExportRow,
  formatLocationName,
  createLocationHistoryEntry,
  removeUndefined,
  createLocationUpdatePayload,
  // ── Builders ──
  buildAuditDescription,
  buildSuccessMessage,
} from "../utils/assetUtils";

// ── Safe string helper (prevents .toLowerCase() crashes on non-strings) ──────
const toSafeString = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
};

// ── PDF dynamic imports ───────────────────────────────────────────────────────
let jsPDF;
let autoTable;
const loadPDFLibraries = async () => {
  try {
    const { default: jsPDFModule } = await import("jspdf");
    jsPDF = jsPDFModule;
    const autoTableModule = await import("jspdf-autotable");
    autoTable = autoTableModule.default;
    return true;
  } catch (err) {
    console.warn("PDF libraries not available:", err.message);
    return false;
  }
};

export const useChangeLocation = () => {
  const { currentUser, loading: authLoading } = useAuth();

  // ── State ──────────────────────────────────────────────────────────────────
  const [allAssets,           setAllAssets]           = useState([]);
  const [locations,           setLocations]           = useState([]);
  const [foundAsset,          setFoundAsset]          = useState(null);
  const [loading,             setLoading]             = useState(true);
  const [isLoadingLocations,  setIsLoadingLocations]  = useState(true);
  const [permissionError,     setPermissionError]     = useState(null);
  const [notification,        setNotification]        = useState(null);
  const [exportData,          setExportData]          = useState([]);
  const [isPreparingExport,   setIsPreparingExport]   = useState(false);
  const [searchTerm,          setSearchTerm]          = useState("");
  const [selectedAssetId,     setSelectedAssetId]     = useState("");
  const [selectedLocation,    setSelectedLocation]    = useState("");
  const [changeReason,        setChangeReason]        = useState("");
  const [quantityToMove,      setQuantityToMove]      = useState(1);
  const [activeTab,           setActiveTab]           = useState(0);
  const [historyPage,         setHistoryPage]         = useState(0);
  const [historyRowsPerPage,  setHistoryRowsPerPage]  = useState(10);
  const [pageReady,           setPageReady]           = useState(false);

  // Child assets
  const [childAssets,        setChildAssets]        = useState([]);
  const [selectedChildIds,   setSelectedChildIds]   = useState([]);
  const [isLoadingChildren,  setIsLoadingChildren]  = useState(false);
  const [showChildMovePanel, setShowChildMovePanel] = useState(false);

  // Filters
  const [filterAssetType,     setFilterAssetType]     = useState("");
  const [filterOwnershipType, setFilterOwnershipType] = useState("");

  // Preferences
  const [assetTypeOptions,     setAssetTypeOptions]     = useState([]);
  const [ownershipTypeOptions, setOwnershipTypeOptions] = useState([]);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);

  // ── Derived state — all via model guards ───────────────────────────────────
  const assetIsDisposed      = isAssetDisposed(foundAsset);
  const assetIsNonSerialized = isNonSerializedAsset(foundAsset);
  const assetIsPrimary       = isPrimaryAsset(foundAsset);
  const assetIsConsumable    = isConsumableAsset(foundAsset);
  const assetIsSparePart     = isSparePartAsset(foundAsset);
  const assetIsMovableChild  = isMovableChildAsset(foundAsset);
  const assetIsExcluded      = isExcludedFromMove(foundAsset);
  const canHaveChildren      = assetIsPrimary;

  console.log("Asset type check:", {
    assetId:       foundAsset?.assetId,
    assetType:     foundAsset?.assetType,
    ownershipType: foundAsset?.ownershipType,
    parentAssetId: foundAsset?.parentAssetId,
    isPrimaryAsset:   assetIsPrimary,
    isConsumable:     assetIsConsumable,
    isSparePart:      assetIsSparePart,
    isMovableChild:   assetIsMovableChild,
  });

  // ── Notification helper ────────────────────────────────────────────────────
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // ── Filtered locations — via model guard ───────────────────────────────────
  const filteredLocations = useMemo(() => {
    if (!Array.isArray(locations)) return [];
    return locations.filter((loc) => {
      if (!loc.locationCode || !loc.locationName) return false;
      if (!isLocationActive(loc))                 return false;
      if (currentUser?.role === USER_ROLES.SUPERADMIN) return true;
      if (
        currentUser?.role === USER_ROLES.ADMIN &&
        currentUser?.companyId &&
        loc.companyId === currentUser.companyId
      ) return true;
      return false;
    });
  }, [locations, currentUser]);

  // ── Filtered assets (safe string coercion before .toLowerCase()) ──────────
  const filteredAssets = useMemo(() => {
    const term = toSafeString(searchTerm).toLowerCase().trim();

    return (Array.isArray(allAssets) ? allAssets : []).filter((a) => {
      if (!a) return false;

      const assetId   = toSafeString(a.assetId).toLowerCase();
      const assetName = toSafeString(a.assetName).toLowerCase();

      const matchesSearch =
        !term || assetId.includes(term) || assetName.includes(term);

            const matchesAssetType =
        !filterAssetType ||
        String(a.assetType || "").toLowerCase() === String(filterAssetType).toLowerCase();

      const matchesOwnershipType =
        !filterOwnershipType ||
        String(a.ownershipType || "").toLowerCase() === String(filterOwnershipType).toLowerCase();

      return matchesSearch && matchesAssetType && matchesOwnershipType;
    });
  }, [allAssets, searchTerm, filterAssetType, filterOwnershipType]);

  // ── History reversed + paginated ──────────────────────────────────────────
  const reversedHistory = useMemo(() => {
    if (!foundAsset?.locationHistory) return [];
    return [...foundAsset.locationHistory].reverse();
  }, [foundAsset]);

  // ── All children selected ──────────────────────────────────────────────────
  const allChildrenSelected = useMemo(
    () =>
      childAssets.length > 0 &&
      childAssets.every((c) => selectedChildIds.includes(c.id)),
    [childAssets, selectedChildIds],
  );

  // ── Export data — via model factory ───────────────────────────────────────
  useEffect(() => {
    if (!foundAsset?.locationHistory) return;
    const formatted = foundAsset.locationHistory
      .map((entry, i) => createExportRow(entry, i))
      .reverse();
    setExportData(formatted);
  }, [foundAsset]);

  // ── Export CSV config — via model headers ─────────────────────────────────
  const exportToCSV = () => ({
    headers: CSV_EXPORT_HEADERS,
    data:    exportData,
  });

  // ── Load locations ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser || authLoading) {
      setIsLoadingLocations(false);
      return;
    }
    const load = async () => {
      try {
        // ✅ locationService.fetchLocations is object-based: { companyId }
        // For superadmin, omit companyId so service returns all locations
        // (service falls back to currentUser.companyId if not given, so we
        // pass an explicit companyId only for non-superadmin roles).
        const query =
          currentUser.role === USER_ROLES.SUPERADMIN
            ? {}
            : { companyId: currentUser.companyId };

        const data = await locationService.fetchLocations(query);
        setLocations(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("[useChangeLocation] fetchLocations error:", err);
        showNotification(`Error loading locations: ${err.message}`, "error");
      } finally {
        setIsLoadingLocations(false);
      }
    };
    load();
  }, [currentUser, authLoading]);

  // ── Load preferences — via model factory ──────────────────────────────────
  // ── Load Asset Type / Ownership Type from single-value-lists ──────────────
  useEffect(() => {
    if (!currentUser || authLoading) {
      setIsLoadingPreferences(false);
      return;
    }
    const loadCatalog = async () => {
      setIsLoadingPreferences(true);
      try {
        const [assetTypes, ownershipTypes] = await Promise.all([
          singleValueListService.getItems("asset_types",     { companyId: currentUser.companyId }),
          singleValueListService.getItems("ownership_types", { companyId: currentUser.companyId }),
        ]);

        setAssetTypeOptions(
          (assetTypes     || []).map((it) => ({
            label: it.label || it.value,
            value: it.value,
          })),
        );
        setOwnershipTypeOptions(
          (ownershipTypes || []).map((it) => ({
            label: it.label || it.value,
            value: it.value,
          })),
        );
      } catch (err) {
        console.error("[useChangeLocation] Failed to load catalogs:", err);
        setAssetTypeOptions([]);
        setOwnershipTypeOptions([]);
      } finally {
        setIsLoadingPreferences(false);
      }
    };
    loadCatalog();
  }, [currentUser, authLoading]);

  // ── Load assets ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!currentUser) {
      setPermissionError(
        "You must be logged in to access asset location changes.",
      );
      setLoading(false);
      setPageReady(true);
      return;
    }
    if (
      currentUser.role !== USER_ROLES.ADMIN &&
      currentUser.role !== USER_ROLES.SUPERADMIN
    ) {
      setPermissionError("Admin or Super Admin permissions required.");
      setLoading(false);
      setPageReady(true);
      return;
    }

    const MIN_MS  = 350;
    const start   = Date.now();
    let cancelled = false;

    const load = async () => {
      try {
        // assetService.fetchAssets is object-based: { companyId }
        // superadmin → omit companyId so service returns all assets
        // admin      → scope to their companyId
        const query =
          currentUser.role === USER_ROLES.SUPERADMIN
            ? {}
            : { companyId: currentUser.companyId };

        const data = await assetService.fetchAssets(query);

        // Normalize assetId / assetName to strings (defensive — keeps the
        // .toLowerCase() safety net that changeLocationService used to provide)
        const normalized = (Array.isArray(data) ? data : [])
          .filter((r) => r && (r.assetId != null || r.id != null))
          .map((r) => ({
            ...r,
            assetId:   String(r.assetId   ?? r.id ?? ""),
            assetName: String(r.assetName ?? ""),
          }));

        if (!cancelled) setAllAssets(normalized);
      } catch (err) {
        console.error("[useChangeLocation] fetchAssets error:", err);
        if (!cancelled) {
          showNotification(`Error loading assets: ${err.message}`, "error");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          const elapsed   = Date.now() - start;
          const remaining = MIN_MS - elapsed;
          if (remaining > 0) {
            setTimeout(() => {
              if (!cancelled) setPageReady(true);
            }, remaining);
          } else {
            setPageReady(true);
          }
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [currentUser, authLoading]);

  // ── Asset selection ────────────────────────────────────────────────────────
  const handleAssetSelect = useCallback(
    async (assetId) => {
      setSelectedAssetId(assetId);

      // Reset child asset state on new selection
      setChildAssets([]);
      setSelectedChildIds([]);
      setShowChildMovePanel(false);

      if (!assetId) {
        setFoundAsset(null);
        setSelectedLocation("");
        setQuantityToMove(1);
        return;
      }

      setLoading(true);
      try {
        const asset = allAssets.find((a) => a.assetId === assetId);
        if (!asset) {
          showNotification("Selected asset not found.", "error");
        } else if (
          currentUser.role === USER_ROLES.ADMIN &&
          asset.companyId !== currentUser.companyId
        ) {
          showNotification("Asset belongs to a different company.", "error");
        } else {
          setFoundAsset(asset);
          setSelectedLocation(asset.locationCode ?? "");
          setQuantityToMove(1);
          setHistoryPage(0);

          // If selected asset is a Primary Asset, load its child assets
          if (asset.assetType === ASSET_TYPES.PRIMARY) {
            setIsLoadingChildren(true);
            try {
              //  assetService.fetchChildAssets — make sure this method exists
              // on AssetService; if not, add it (see note below).
              const children = await assetService.fetchChildAssets(asset.assetId);
              const safeChildren = Array.isArray(children) ? children : [];
              setChildAssets(safeChildren);
              // Default: select all children
              setSelectedChildIds(safeChildren.map((c) => c.id));
              if (safeChildren.length > 0) {
                setShowChildMovePanel(true);
              }
            } catch (err) {
              console.warn(
                "[useChangeLocation] fetchChildAssets error:", err,
              );
              setChildAssets([]);
              setSelectedChildIds([]);
            } finally {
              setIsLoadingChildren(false);
            }
          }
        }
      } catch (err) {
        console.error("[useChangeLocation] handleAssetSelect error:", err);
        showNotification(`Error fetching asset: ${err.message}`, "error");
      } finally {
        setLoading(false);
      }
    },
    [allAssets, currentUser],
  );

  // ── Toggle individual child asset selection ────────────────────────────────
  const handleToggleChildAsset = useCallback((childId) => {
    setSelectedChildIds((prev) =>
      prev.includes(childId)
        ? prev.filter((id) => id !== childId)
        : [...prev, childId],
    );
  }, []);

  // ── Select / Deselect all children ────────────────────────────────────────
  const handleToggleAllChildren = useCallback(() => {
    if (allChildrenSelected) {
      setSelectedChildIds([]);
    } else {
      setSelectedChildIds(childAssets.map((c) => c.id));
    }
  }, [allChildrenSelected, childAssets]);

  // ── Change location ────────────────────────────────────────────────────────
  const handleChangeLocation = async () => {
    if (!foundAsset)
      return showNotification("Select an asset first.", "error");
    if (assetIsDisposed)
      return showNotification(
        "Cannot change location for disposed assets.",
        "error",
      );

    if (assetIsExcluded)
      return showNotification(
        `${foundAsset.assetType}s are managed through the Consumptions module and cannot be moved here.`,
        "error",
      );

    if (!selectedLocation)
      return showNotification("Select a new location.", "error");
    if (!changeReason.trim())
      return showNotification("Enter a reason.", "error");

    if (assetIsNonSerialized) {
      if (quantityToMove < 1)
        return showNotification("Quantity must be at least 1.", "error");
      if (quantityToMove > (foundAsset.quantity || 0))
        return showNotification(
          `Cannot move ${quantityToMove}. Only ${foundAsset.quantity || 0} available.`,
          "error",
        );
    }

    const locData = filteredLocations.find(
      (l) => l.locationCode === selectedLocation,
    );
    if (!locData) return showNotification("Invalid location.", "error");
    if (foundAsset.locationCode === selectedLocation)
      return showNotification(
        "Asset is already at that location.",
        "info",
      );

    setLoading(true);
    try {
      const newLocationName = formatLocationName(locData);
      const now             = new Date().toISOString();
      const changedBy       = currentUser?.email ?? currentUser?.uid;

      const locationEntry = createLocationHistoryEntry({
        from:                 foundAsset.currentLocationName ?? "N/A",
        to:                   newLocationName,
        changedAt:            new Date(),
        changedBy,
        reason:               changeReason,
        previousLocationCode: foundAsset.locationCode,
        ...(assetIsNonSerialized && { quantityMoved: quantityToMove }),
      });

      const updateData = removeUndefined(
        createLocationUpdatePayload({
          locationCode:        selectedLocation,
          locationName:        locData.locationName,
          locationAddress:     locData.address,
          locationCity:        locData.city,
          locationState:       locData.state,
          locationZipCode:     locData.zipCode,
          currentLocationName: newLocationName,
          updatedAt:           now,
          updatedBy:           changedBy,
          locationHistory: [
            ...(foundAsset.locationHistory ?? []),
            locationEntry,
          ],
          ...(assetIsNonSerialized &&
            quantityToMove < (foundAsset.quantity || 0) && {
              lastPartialMoveQuantity: quantityToMove,
            }),
        }),
      );

    
      await assetService.upsertData(foundAsset.id, {
        ...foundAsset,
        ...updateData,
      });


      // Move selected child assets (Sub Asset, Component, Accessory) together
      if (canHaveChildren && selectedChildIds.length > 0) {
        const childLocationEntry = createLocationHistoryEntry({
          from:                 foundAsset.currentLocationName ?? "N/A",
          to:                   newLocationName,
          changedAt:            new Date(),
          changedBy,
          reason: `Moved with Primary Asset: ${foundAsset.assetName} — ${changeReason}`,
          previousLocationCode: foundAsset.locationCode,
        });

        const childUpdatePromises = childAssets
          .filter((c) => selectedChildIds.includes(c.id))
          .map((child) =>
            assetService.upsertData(child.id, {
              ...child,
              ...removeUndefined(
                createLocationUpdatePayload({
                  locationCode:        selectedLocation,
                  locationName:        locData.locationName,
                  locationAddress:     locData.address,
                  locationCity:        locData.city,
                  locationState:       locData.state,
                  locationZipCode:     locData.zipCode,
                  currentLocationName: newLocationName,
                  updatedAt:           now,
                  updatedBy:           changedBy,
                  locationHistory: [
                    ...(child.locationHistory ?? []),
                    childLocationEntry,
                  ],
                }),
              ),
            }),
          );

        const results = await Promise.allSettled(childUpdatePromises);
        const failed  = results.filter((r) => r.status === "rejected");
        if (failed.length > 0) {
          console.warn(
            `[useChangeLocation] ${failed.length} child asset(s) failed to update.`,
          );
          showNotification(
            `Primary asset moved but ${failed.length} child asset(s) failed to update.`,
            "warning",
          );
        }

        setChildAssets((prev) =>
          prev.map((c) =>
            selectedChildIds.includes(c.id)
              ? {
                  ...c,
                  locationCode:        selectedLocation,
                  currentLocationName: newLocationName,
                  updatedAt:           new Date(),
                }
              : c,
          ),
        );
      }

      const updatedAsset = {
        ...foundAsset,
        ...updateData,
        updatedAt: new Date(),
      };
      setFoundAsset(updatedAsset);
      setAllAssets((prev) =>
        prev.map((a) => (a.id === updatedAsset.id ? updatedAsset : a)),
      );

      // Audit log
      try {
        await logAuditEvent({
          actionType:  AUDIT_ACTION_TYPES.LOCATION_CHANGE,
          entityType:  AUDIT_ENTITY_TYPES.ASSET,
          entityId:    foundAsset.id,
          description: buildAuditDescription({
            assetName:       foundAsset.assetName,
            from:            locationEntry.from,
            to:              locationEntry.to,
            reason:          changeReason,
            isNonSerialized: assetIsNonSerialized,
            quantityToMove,
            hasChildren:     canHaveChildren,
            childCount:      selectedChildIds.length,
          }),
          companyId: foundAsset.companyId,
          userId:    currentUser?.uid,
        });
      } catch (auditErr) {
        console.warn("[useChangeLocation] Audit log failed:", auditErr);
      }

      showNotification(
        buildSuccessMessage({
          hasChildren:       canHaveChildren,
          childCount:        selectedChildIds.length,
          isNonSerialized:   assetIsNonSerialized,
          quantityToMove,
          unitOfMeasurement: foundAsset.unitOfMeasurement,
        }),
        "success",
      );

      setChangeReason("");
      setQuantityToMove(1);
    } catch (err) {
      console.error("[useChangeLocation] handleChangeLocation error:", err);
      showNotification(`Error changing location: ${err.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Undo last change ───────────────────────────────────────────────────────
  const handleUndoLastChange = async () => {
    if (!foundAsset?.locationHistory?.length)
      return showNotification("No previous history to undo.", "info");
    if (assetIsDisposed)
      return showNotification("Cannot undo for disposed assets.", "error");
    if (
      !window.confirm(
        "Are you sure you want to undo the last location change?",
      )
    ) return;

    setLoading(true);
    try {
      const newHistory   = foundAsset.locationHistory.slice(0, -1);
      const last         = foundAsset.locationHistory.at(-1);
      const previousCode = last.previousLocationCode ?? "";

      const updateData = {
        locationCode:        previousCode,
        currentLocationName: last.from ?? "",
        updatedAt:           new Date().toISOString(),
        updatedBy:           currentUser?.email ?? currentUser?.uid,
        locationHistory:     newHistory,
        ...(assetIsNonSerialized && last.quantityMoved
          ? { quantity: foundAsset.quantity || 0 }
          : {}),
      };

    
      await assetService.upsertData(foundAsset.id, {
        ...foundAsset,
        ...updateData,
      });

      const updated = { ...foundAsset, ...updateData, updatedAt: new Date() };
      setFoundAsset(updated);
      setAllAssets((prev) =>
        prev.map((a) => (a.id === updated.id ? updated : a)),
      );
      setSelectedLocation(previousCode);
      setChangeReason("");
      showNotification("Last location change has been undone.", "success");
    } catch (err) {
      console.error("[useChangeLocation] handleUndoLastChange error:", err);
      showNotification("Failed to undo last change", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── PDF export ─────────────────────────────────────────────────────────────
  const exportToPDF = async () => {
    if (!foundAsset?.locationHistory?.length)
      return showNotification("No location history to export", "info");
    try {
      if (!(await loadPDFLibraries())) {
        showNotification("PDF export not available", "error");
        return;
      }
      setIsPreparingExport(true);
      const pdfDoc = new jsPDF();
      pdfDoc.setFontSize(16);
      pdfDoc.text(
        `Location History for ${foundAsset.assetName} (${foundAsset.assetId})`,
        14,
        20,
      );

      const body = foundAsset.locationHistory
        .map((e, i) => {
          const row = createExportRow(e, i);
          return [
            row["S.No"],
            row["From Location"],
            row["To Location"],
            row["Changed By"],
            row["Change Date"],
            row["Reason"],
            row["Quantity Moved"],
          ];
        })
        .reverse();

      autoTable(pdfDoc, {
        startY:       30,
        head:         [["S.No", "From", "To", "By", "Date", "Reason", "Qty"]],
        body,
        theme:        "grid",
        styles:       { fontSize: 8 },
        headStyles:   { fillColor: [21, 101, 192], fontSize: 9 },
        columnStyles: { 0: { cellWidth: 10 }, 5: { cellWidth: 40 } },
      });

      pdfDoc.save(`${foundAsset.assetId}_location_history.pdf`);
      showNotification("PDF exported successfully", "success");
    } catch (err) {
      console.error("[useChangeLocation] exportToPDF error:", err);
      showNotification("Failed to generate PDF", "error");
    } finally {
      setIsPreparingExport(false);
    }
  };

  // ── Return ─────────────────────────────────────────────────────────────────
  return {
    // Data
    allAssets,
    filteredAssets,
    filteredLocations,
    foundAsset,
    reversedHistory,
    exportData,

    // Child assets
    childAssets,
    selectedChildIds,
    isLoadingChildren,
    showChildMovePanel,
    allChildrenSelected,

    // Preference-based dropdown options
    assetTypeOptions,
    ownershipTypeOptions,
    isLoadingPreferences,

    // Status
    loading,
    authLoading,
    isLoadingLocations,
    isPreparingExport,
    permissionError,
    notification,
    pageReady,

    // Asset type flags
    isPrimaryAsset:     assetIsPrimary,
    isConsumable:       assetIsConsumable,
    isSparePart:        assetIsSparePart,
    isMovableChild:     assetIsMovableChild,
    isExcludedFromMove: assetIsExcluded,
    canHaveChildren,
    isAssetDisposed:    assetIsDisposed,
    isNonSerialized:    assetIsNonSerialized,

    // UI state
    searchTerm,          setSearchTerm,
    selectedAssetId,
    selectedLocation,    setSelectedLocation,
    changeReason,        setChangeReason,
    quantityToMove,      setQuantityToMove,
    activeTab,           setActiveTab,
    historyPage,         setHistoryPage,
    historyRowsPerPage,  setHistoryRowsPerPage,
    setIsPreparingExport,

    // Filters
    filterAssetType,     setFilterAssetType,
    filterOwnershipType, setFilterOwnershipType,

    // Actions
    handleAssetSelect,
    handleChangeLocation,
    handleUndoLastChange,
    handleToggleChildAsset,
    handleToggleAllChildren,
    exportToPDF,
    exportToCSV,

    // Notification
    setNotification,
  };
};